import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames/bind";
import { EditColumnsModal } from "components/common/edit-columns-modal";
import { pluralize } from "utils/string-helpers";
import FaPencil from "react-icons/fa/pencil";
import FaFilter from "react-icons/fa/filter";
import FaBook from "react-icons/fa/book";
import FaTrash from "react-icons/fa/trash";
import FaDocument from "react-icons/fa/file-text-o";
import styles from "components/documents/data-grid-actions-panel.scss";
import { currencyType, paginationType, categoryType, documentFiltersType } from "custom-prop-types";
import { ExportButton } from "components/documents/exports/export-button";
import { preferredCurrencySymbol } from "utils/currency-symbol";
import { findIntegrationInUse } from "utils/check-integration";
import { MultiDocumentActionsContainer } from "./multi-document-actions-container";

export const DataGridActionsPanel = (props) => (
  <div className="documents5">
    <StandardActionPanel { ...props } selectedDocumentsCount={ props.selectedDocumentIds.length } />
    <MultiDocumentActionsContainer { ...props } userEmail={ props.currentUser.email } />
  </div>
);

DataGridActionsPanel.propTypes = {
  currentUser: PropTypes.shape({
    email: PropTypes.string
  }).isRequired,
  selectedDocumentIds: PropTypes.arrayOf(PropTypes.string).isRequired
};

const StandardActionPanel = (props) => {
  const {
    columns,
    currencies,
    filtersCount,
    isDocumentViewerVisible,
    isEditColumnsVisible,
    isGLCodesVisible,
    isTrash,
    onAddDocumentType,
    onHideDocumentViewer,
    onHideEditColumns,
    onHideGLCodesPanel,
    onRemoveDocumentType,
    onReturnToDefaultColumns,
    onShowDocumentViewer,
    onShowEditColumns,
    onShowGLCodesPanel,
    onStartDraggableMode,
    onStartFiltersDrawerMode,
    onToggleColumn,
    pagination,
    preferredCurrency,
    selectedDocumentsCount
  } = props;
  const integration = findIntegrationInUse(props.categories);
  const bindedClassnames = classnames.bind(styles);
  const actionsPanelClassNames = bindedClassnames({
    trash: isTrash,
    "standard-action-panel__container": true
  });
  if (selectedDocumentsCount > 0) {
    return null;
  }

  return (
    <div className={ actionsPanelClassNames }>
      <div className={ styles["results-found"] }>
        { documentsInformationLabel(pagination, currencies, preferredCurrency, isTrash) }
        <div className={ styles.actions }>
          { renderEditGLCodesButton({
            integration,
            isGLCodesVisible,
            isTrash,
            onHideDocumentViewer,
            onHideGLCodesPanel,
            onRemoveDocumentType,
            onShowGLCodesPanel,
            onAddDocumentType
          })}
          { renderShowDocumentViewerButton(
              isDocumentViewerVisible,
              isGLCodesVisible,
              onShowDocumentViewer,
              onStartDraggableMode
          )}
          <button onClick={ onShowEditColumns }>
            <FaPencil className={ styles.icon } size={ 15 } />
            Edit columns
          </button>
          <button onClick={ onStartFiltersDrawerMode }>
            <FaFilter className={ styles.icon } size={ 15 } />
            { renderTextForFilterButton(filtersCount) }
          </button>
          { renderButtonsForTrash(props) }
        </div>
      </div>
      <EditColumnsModal
        columns={ columns }
        isEditColumnsVisible={ isEditColumnsVisible }
        onClose={ onHideEditColumns }
        onReturnToDefaultColumns={ onReturnToDefaultColumns }
        onToggleColumn={ onToggleColumn }
      />
    </div>
  );
};

StandardActionPanel.propTypes = {
  categories: PropTypes.arrayOf(categoryType).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  currencies: PropTypes.arrayOf(currencyType).isRequired,
  filtersCount: PropTypes.number.isRequired,
  isDocumentViewerVisible: PropTypes.bool.isRequired,
  isEditColumnsVisible: PropTypes.bool.isRequired,
  isGLCodesVisible: PropTypes.bool.isRequired,
  isTrash: PropTypes.bool.isRequired,
  onAddDocumentType: PropTypes.func.isRequired,
  onHideDocumentViewer: PropTypes.func.isRequired,
  onHideEditColumns: PropTypes.func.isRequired,
  onHideGLCodesPanel: PropTypes.func.isRequired,
  onRemoveDocumentType: PropTypes.func.isRequired,
  onReturnToDefaultColumns: PropTypes.func.isRequired,
  onShowDocumentViewer: PropTypes.func.isRequired,
  onShowEditColumns: PropTypes.func.isRequired,
  onShowGLCodesPanel: PropTypes.func.isRequired,
  onStartDraggableMode: PropTypes.func.isRequired,
  onStartFiltersDrawerMode: PropTypes.func.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  pagination: paginationType.isRequired,
  preferredCurrency: PropTypes.string.isRequired,
  selectedDocumentsCount: PropTypes.number.isRequired
};

StandardActionPanel.defaultProps = {
  glCodes: []
};

const documentsInformationLabel = (pagination, currencies, preferredCurrency, isTrash) => {
  const documentsFound = pluralize(pagination.totalCountFiltered, "documents", "document");
  const currencySymbol = preferredCurrencySymbol(currencies, preferredCurrency);
  const total = pagination.totalSumFiltered;
  const currencyCode = preferredCurrency === "AUD" || preferredCurrency === "NZD" ? "" : preferredCurrency;
  const trash = isTrash ? " in trash" : "";
  return (
    <div>
      {documentsFound}{trash}
      <span className={ styles.total }>Total: { currencySymbol}{total} {currencyCode}</span>
    </div>
  );
};

export const renderTextForFilterButton = (filtersCount) => {
  if (filtersCount === 0) {
    return "Filters";
  }
  return (
    <span className={ styles["active-filters"] }>
      { pluralize(filtersCount, "filters applied", "filter applied", true) }
    </span>
  );
};

export const renderShowDocumentViewerButton = (
  isDocumentViewerVisible,
  isGLCodesVisible,
  onShowDocumentViewer,
  onStartDraggableMode
) => {
  if (isDocumentViewerVisible) {
    return null;
  }
  const click = isGLCodesVisible ? () => { onStartDraggableMode(); onShowDocumentViewer(); } : onShowDocumentViewer;
  return (
    <button onClick={ click }>
      <FaDocument className={ styles.icon } height={ 15 } />
      Show document viewer
    </button>
  );
};

const renderEditGLCodesButton = (props) => {
  if (props.integration) {
    return toggleGLCodesButton(props);
  }
  return null;
};

const toggleGLCodesButton = ({
  isGLCodesVisible,
  isTrash,
  onAddDocumentType,
  onHideDocumentViewer,
  onHideGLCodesPanel,
  onRemoveDocumentType,
  onShowGLCodesPanel
}) => {
  if (isTrash) {
    return null;
  }
  if (isGLCodesVisible) {
    return (
      <button onClick={ () => { onHideGLCodesPanel(); onRemoveDocumentType(); } }>
        <FaBook className={ styles.icon } height={ 15 } />
        Hide GL codes panel
      </button>
    );
  }
  return (
    <button
      onClick={ () => {
        onHideDocumentViewer();
        onShowGLCodesPanel();
        onAddDocumentType({ key: "receipt" });
      } }
    >
      <FaBook className={ styles.icon } height={ 15 } />
      Edit GL codes
      </button>
  );
};

toggleGLCodesButton.propTypes = {
  isGLCodesVisible: PropTypes.bool.isRequired,
  isTrash: PropTypes.bool.isRequired,
  onAddDocumentType: PropTypes.func.isRequired,
  onHideDocumentViewer: PropTypes.func.isRequired,
  onHideGLCodesPanel: PropTypes.func.isRequired,
  onRemoveDocumentType: PropTypes.func.isRequired,
  onShowGLCodesPanel: PropTypes.func.isRequired
};

const renderExportAllButton = (
  accountId,
  accountSlug,
  filters,
  glCodes,
  onExportAll,
  onLinkAccount,
  onStartExportMode
) => {
  if (filters.type !== "other") {
    return (
      <ExportButton
        accountSlug={ accountSlug }
        glCodes={ glCodes }
        onLinkAccount={ onLinkAccount }
        onStartExportMode={ onStartExportMode }
        selectedDocumentIds={ [] }
      />
    );
  }
  return null;
};

const renderButtonsForTrash = (props) => {
  if (props.isTrash) {
    return (
      <button className={ styles["exit-trash"] } onClick={ props.onExitTrash }>
        <FaTrash className={ styles.icon } size={ 15 } />
        Exit trash
      </button>
    );
  }

  if (props.isGLCodesVisible) {
    return renderExportAllButton(
      props.accountId,
      props.accountSlug,
      props.filters,
      props.glCodes,
      props.onExportAll,
      props.onLinkAccount,
      props.onStartExportMode
    );
  }

  return (
    <div className={ styles.buttons }>
      <button onClick={ props.onShowTrash }>
        <FaTrash className={ styles.icon } size={ 15 } />
      View trash
    </button>
      {renderExportAllButton(
        props.accountId,
        props.accountSlug,
        props.filters,
        props.glCodes,
        props.onExportAll,
        props.onLinkAccount,
        props.onStartExportMode
      )}
    </div>
  );
};

renderButtonsForTrash.propTypes = {
  accountId: PropTypes.string.isRequired,
  accountSlug: PropTypes.string.isRequired,
  filters: documentFiltersType.isRequired,
  glCodes: PropTypes.arrayOf(categoryType),
  isGLCodesVisible: PropTypes.bool.isRequired,
  isTrash: PropTypes.bool.isRequired,
  onExitTrash: PropTypes.func.isRequired,
  onExportAll: PropTypes.func.isRequired,
  onLinkAccount: PropTypes.func.isRequired,
  onShowTrash: PropTypes.func.isRequired,
  onStartExportMode: PropTypes.func.isRequired
};



// WEBPACK FOOTER //
// ./src/js/components/documents/documents-grid/data-grid-actions-panel.jsx